/**
 * Shop By Category – 4.5 peek, infinite loop slider
 */
(function () {
  'use strict';

  function debounce(fn, delay) {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(null, args), delay);
    };
  }

  function initSlider(root) {
    if (!root || root.dataset.sbcReady === 'true') return;

    const track = root.querySelector('[data-sbc-track]');
    const prevBtn = root.querySelector('[data-sbc-prev]');
    const nextBtn = root.querySelector('[data-sbc-next]');
    const enableLoop = root.dataset.loop !== 'false';

    if (!track) return;

    let originalCount = 0;
    let trackIndex = 0;
    let isAnimating = false;
    let animTimer = null;

    function getOriginalSlides() {
      return Array.from(track.querySelectorAll('.sbc-slide:not(.sbc-slide--clone)'));
    }

    function getGap() {
      return parseFloat(getComputedStyle(track).gap) || 20;
    }

    function getSlideStep() {
      const slide = track.querySelector('.sbc-slide');
      if (!slide) return 0;
      return slide.offsetWidth + getGap();
    }

    function clearAnimLock() {
      isAnimating = false;
      if (animTimer) {
        clearTimeout(animTimer);
        animTimer = null;
      }
    }

    function lockAnim() {
      isAnimating = true;
      if (animTimer) clearTimeout(animTimer);
      animTimer = setTimeout(clearAnimLock, 550);
    }

    function clearClones() {
      track.querySelectorAll('.sbc-slide--clone').forEach((el) => el.remove());
    }

    function setNavLoopMode() {
      const show = enableLoop && originalCount >= 2;
      if (prevBtn) {
        prevBtn.disabled = false;
        prevBtn.hidden = !show;
      }
      if (nextBtn) {
        nextBtn.disabled = false;
        nextBtn.hidden = !show;
      }
    }

    function setNavPagedMode(index, maxIndex) {
      const visible = getSlidesVisible();
      const hideNav = originalCount <= visible;

      if (prevBtn) {
        prevBtn.disabled = index <= 0;
        prevBtn.hidden = hideNav;
      }
      if (nextBtn) {
        nextBtn.disabled = index >= maxIndex;
        nextBtn.hidden = hideNav;
      }
    }

    function getSlidesVisible() {
      const width = window.innerWidth;
      const custom = parseFloat(root.dataset.slidesVisible);
      if (width <= 767) return 1.15;
      if (width <= 1199) return 2.5;
      return Number.isFinite(custom) ? custom : 4.5;
    }

    function applyTransform(index, animate) {
      const step = getSlideStep();
      if (!step) return;

      track.classList.toggle('is-resetting', !animate);
      track.style.transform = `translate3d(-${index * step}px, 0, 0)`;
      trackIndex = index;

      if (!animate) {
        requestAnimationFrame(() => track.classList.remove('is-resetting'));
      }

      if (!animate && enableLoop && originalCount >= 2 && index >= originalCount) {
        applyTransform(index - originalCount, false);
      }
    }

    function buildLoop() {
      clearClones();
      originalCount = getOriginalSlides().length;

      if (!enableLoop || originalCount < 2) {
        trackIndex = 0;
        applyTransform(0, false);
        const visible = getSlidesVisible();
        const maxIndex = Math.max(0, originalCount - Math.floor(visible));
        setNavPagedMode(trackIndex, maxIndex);
        return;
      }

      getOriginalSlides().forEach((slide) => {
        const clone = slide.cloneNode(true);
        clone.classList.add('sbc-slide--clone');
        clone.setAttribute('aria-hidden', 'true');
        track.appendChild(clone);
      });

      trackIndex = 0;
      applyTransform(0, false);
      setNavLoopMode();
    }

    function snapIfNeeded() {
      if (!enableLoop || originalCount < 2) return;
      if (trackIndex >= originalCount) {
        applyTransform(trackIndex - originalCount, false);
      }
    }

    function onTransitionEnd(event) {
      if (event.target !== track || event.propertyName !== 'transform') return;
      clearAnimLock();
      snapIfNeeded();
    }

    function move(direction) {
      if (isAnimating || originalCount < 1) return;

      if (enableLoop && originalCount >= 2) {
        if (direction === 1 && trackIndex === originalCount - 1) {
          lockAnim();
          applyTransform(originalCount, true);
          return;
        }

        if (direction === -1 && trackIndex === 0) {
          applyTransform(originalCount, false);
          requestAnimationFrame(() => {
            lockAnim();
            applyTransform(originalCount - 1, true);
          });
          return;
        }

        lockAnim();
        applyTransform(trackIndex + direction, true);
        return;
      }

      const visible = getSlidesVisible();
      const maxIndex = Math.max(0, originalCount - Math.floor(visible));
      const next = Math.max(0, Math.min(trackIndex + direction, maxIndex));
      lockAnim();
      applyTransform(next, true);
      setNavPagedMode(next, maxIndex);
    }

    function refresh() {
      buildLoop();
      requestAnimationFrame(() => {
        applyTransform(trackIndex, false);
        snapIfNeeded();
      });
    }

    track.addEventListener('transitionend', onTransitionEnd);

    if (prevBtn) prevBtn.addEventListener('click', () => move(-1));
    if (nextBtn) nextBtn.addEventListener('click', () => move(1));

    window.addEventListener('resize', debounce(refresh, 150));

    if (typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver(debounce(refresh, 150));
      const viewport = root.querySelector('[data-sbc-viewport]');
      if (viewport) ro.observe(viewport);
    }

    root.dataset.sbcReady = 'true';
    refresh();
  }

  function initAll() {
    document.querySelectorAll('[data-shop-by-category]').forEach((root) => {
      root.dataset.sbcReady = 'false';
      initSlider(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const root = event.target.querySelector('[data-shop-by-category]');
    if (root) {
      root.dataset.sbcReady = 'false';
      initSlider(root);
    }
  });
})();
(function () {
  'use strict';
  function addSwipe(root) {
    if (!root || root.dataset.sbcSwipe === 'true') return;
    var vp = root.querySelector('[data-sbc-viewport]');
    var prev = root.querySelector('[data-sbc-prev]');
    var next = root.querySelector('[data-sbc-next]');
    if (!vp || !prev || !next) return;

    var x0 = 0, y0 = 0, locked = null;

    vp.addEventListener('touchstart', function (e) {
      if (e.touches.length !== 1) return;
      x0 = e.touches[0].clientX;
      y0 = e.touches[0].clientY;
      locked = null;
    }, { passive: true });

    vp.addEventListener('touchmove', function (e) {
      if (locked !== null || e.touches.length !== 1) return;
      var dx = e.touches[0].clientX - x0;
      var dy = e.touches[0].clientY - y0;
      if (Math.abs(dx) > 10 || Math.abs(dy) > 10) {
        locked = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
      }
    }, { passive: true });

    vp.addEventListener('touchend', function (e) {
      if (locked !== 'x') return;
      var dx = e.changedTouches[0].clientX - x0;
      if (Math.abs(dx) < 45) return;
      var btn = dx < 0 ? next : prev;
      if (!btn.disabled && !btn.hidden) btn.click();
    }, { passive: true });

    vp.style.touchAction = 'pan-y';
    root.dataset.sbcSwipe = 'true';
  }

  function initSwipeAll() {
    document.querySelectorAll('[data-shop-by-category]').forEach(addSwipe);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initSwipeAll);
  } else {
    initSwipeAll();
  }
  document.addEventListener('shopify:section:load', initSwipeAll);
})();